import {CAMPAIGN_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {Nodes} from 'common/src/modules/decisionTree/decisionTree.types';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _flatten from 'lodash/flatten';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';
import _sumBy from 'lodash/sumBy';
import {DateTime} from 'luxon';
import {PERCENT_METRIC_TYPES} from '../analytics/analytics.constants';
import {MetricValue} from '../analytics/analytics.types';
import getAnalyticsUIName from '../analytics/utils/getAnalyticsUIName';
import {rationInsights} from './campaignAnalytics.constants';
import {
  AvailableMetricsForPreset,
  FALLBACK_OUTPUT_ID,
  InsightsData,
  InsightsMetricsData,
  NodeInsights,
  NodesInsightsOutputInputData,
  RemoteAvailableFilter,
  RemoteAvailableMetricsForPreset,
  RemoteDetailedExperiment,
  RemoteDetailedExperimentGraphData,
  RemoteDetailedExperimentStagesData,
  RemoteExperiment,
  RemoteGeneralStatistics,
  RemoteNodeInsightsData,
  RemoteNodeInsightsDataWithSource,
  UNSEEN_NODES_OUTPUT_ID,
} from './campaignAnalytics.types';
import {getEmptyInsightsObj} from './utils/getEmptyInsightsObj';
import {getInOutDataObject} from './utils/getInOutDataObject';
import {getNodeInsightsByOutputs} from './utils/getNodeInsightsByOutputs';
import {featureFlags} from '@cl/shared';
import {FeatureFlagKey} from '../featureFlags/featureFlags.types';
import {CLSelectorOption} from 'common/src/components/Selectors/CLSelector/CLSelector.types';
import isPercentageMetric from '../analytics/utils/isPercentageMetric';
import {AvailableMetricWithTotal} from '../campaigns/store/insightsPage/insightsPage.types';

const {enabledFlagsSubject} = featureFlags;

function toLocalFlightControlInsights(
  remote: InsightsMetricsData,
  metrics: MetricValue[],
): InsightsData {
  return _reduce(
    metrics,
    (acc, metric: MetricValue) => {
      const shouldMultiplyValue = rationInsights.includes(metric);

      return {
        ...acc,
        [metric]: remote?.MetricsData?.[metric] * (shouldMultiplyValue ? 100 : 1) || 0,
      };
    },
    {} as InsightsData,
  );
}

function toNodesAggregatedInsights({
  nodes,
  nodesIdsForCampaignInsights,
  insights: remoteInsights,
  nodeInsights,
  campaignTotals,
  fallbackCreativeNodeId,
  metrics,
}: {
  nodes: Nodes;
  nodesIdsForCampaignInsights: number[];
  insights: RemoteNodeInsightsDataWithSource[];
  nodeInsights: RemoteNodeInsightsData[];
  campaignTotals: InsightsMetricsData;
  fallbackCreativeNodeId: number;
  metrics: MetricValue[];
}): NodesInsightsOutputInputData {
  const rootNodeId = nodes[CAMPAIGN_NODE_ID].outputs?.[0].nodeId;
  const rootNodeInsights = _find(nodeInsights, (insights) => insights.targetNodeId === rootNodeId);

  const groupedNodes: Record<string, RemoteNodeInsightsDataWithSource[]> = getNodeInsightsByOutputs(
    {
      insights: remoteInsights,
      nodes,
      fallbackCreativeNodeId,
      nodesIdsForCampaignInsights,
    },
  );

  const insights: RemoteNodeInsightsDataWithSource[] = _flatten(_map(groupedNodes));

  const maxRatios = _reduce(
    insights,
    (
      result: {
        [MetricValue.ClickThroughRate]: number;
        [MetricValue.ConversionRate]: number;
        [MetricValue.VideoCompletionRate]: number;
      },
      value: RemoteNodeInsightsData,
    ) => {
      if (result[MetricValue.ClickThroughRate] < value?.MetricsData?.[MetricValue.ClickThroughRate])
        result[MetricValue.ClickThroughRate] = value?.MetricsData?.[MetricValue.ClickThroughRate];
      if (result[MetricValue.ConversionRate] < value?.MetricsData?.[MetricValue.ConversionRate])
        result[MetricValue.ConversionRate] = value?.MetricsData?.[MetricValue.ConversionRate];
      if (
        result[MetricValue.VideoCompletionRate] <
        value?.MetricsData?.[MetricValue.VideoCompletionRate]
      )
        result[MetricValue.VideoCompletionRate] =
          value?.MetricsData?.[MetricValue.VideoCompletionRate];

      return result;
    },
    {
      [MetricValue.ClickThroughRate]: 0,
      [MetricValue.ConversionRate]: 0,
      [MetricValue.VideoCompletionRate]: 0,
    },
  );

  const result: {
    outgoingData: Record<number, NodeInsights>;
    incomingData: Record<number, NodeInsights>;
  } = {
    outgoingData: {},
    incomingData: {},
  };

  insights.forEach((insight) => {
    if (!result.outgoingData[insight.sourceNodeId]) {
      result.outgoingData[insight.sourceNodeId] = {
        totals: getEmptyInsightsObj(metrics),
        outputs: [],
      };
    }

    result.outgoingData[insight.sourceNodeId] = result.outgoingData[insight.sourceNodeId] || {
      totals: result.outgoingData[insight.sourceNodeId].totals,
      outputs: [],
    };

    const outputId =
      insight.targetNodeId === fallbackCreativeNodeId ? FALLBACK_OUTPUT_ID : insight.outputId;

    setOutgoingDataTotals(insight);
    setOutgoingDataOutputs(insight, outputId);
  });

  nodeInsights.forEach((nodeInsight) => {
    result.incomingData[nodeInsight.targetNodeId] = {
      totals: getEmptyInsightsObj(metrics),
      unseen: getEmptyInsightsObj(metrics),
    };

    metrics.forEach((metric) => {
      if (PERCENT_METRIC_TYPES.includes(metric)) {
        result.incomingData[nodeInsight.targetNodeId].totals[metric] = {
          value: nodeInsight?.MetricsData?.[metric],
          percentageOfTotal:
            nodeInsight?.MetricsData?.[metric] / maxRatios[metric as keyof typeof maxRatios],
        };
      } else {
        result.incomingData[nodeInsight.targetNodeId].totals[metric] = getInOutDataObject(
          nodeInsight?.MetricsData?.[metric],
          campaignTotals?.MetricsData?.[metric],
        );

        const filtered = _filter(
          insights,
          (insight) => insight.targetNodeId === nodeInsight.targetNodeId,
        );

        const insightsComingFromVisibleNodes = _reduce(
          filtered,
          (acc, val) => {
            return acc + (val?.MetricsData?.[metric] || 0);
          },
          0,
        );

        if (nodeInsight.targetNodeId !== rootNodeId) {
          result.incomingData[nodeInsight.targetNodeId].unseen[metric].value =
            result.incomingData[nodeInsight.targetNodeId].totals[metric].value -
            insightsComingFromVisibleNodes;
        }
      }
    });
  });

  setCampaignNodeTotals();

  function setOutgoingDataTotals(insight: RemoteNodeInsightsDataWithSource) {
    metrics.forEach((metric) => {
      if (PERCENT_METRIC_TYPES.includes(metric)) {
        const nodeInsightsData = _find(nodeInsights, {targetNodeId: insight.sourceNodeId});

        result.outgoingData[insight.sourceNodeId].totals[metric].value =
          nodeInsightsData?.MetricsData?.[metric];

        result.outgoingData[insight.sourceNodeId].totals[metric].percentageOfTotal =
          campaignTotals?.MetricsData?.[metric];
      } else {
        const value = _sumBy(groupedNodes[insight.sourceNodeId], (node) => {
          if (!node.MetricsData) {
            return 0;
          }

          return node.MetricsData[metric];
        });

        result.outgoingData[insight.sourceNodeId].totals[metric].value = value;

        result.outgoingData[insight.sourceNodeId].totals[metric].percentageOfTotal =
          result.outgoingData[insight.sourceNodeId].totals[metric].value /
          campaignTotals?.MetricsData?.[metric];
      }
    });
  }

  function setOutgoingDataOutputs(insight: RemoteNodeInsightsDataWithSource, outputId: number) {
    result.outgoingData[insight.sourceNodeId].outputs[outputId] =
      result.outgoingData[insight.sourceNodeId].outputs[outputId] || {};

    metrics.forEach((metric) => {
      if (PERCENT_METRIC_TYPES.includes(metric)) {
        result.outgoingData[insight.sourceNodeId].outputs[outputId][metric] = {
          value: insight?.MetricsData?.[metric],
          percentageOfTotal:
            insight?.MetricsData?.[metric] / maxRatios[metric as keyof typeof maxRatios],
        };
      } else {
        result.outgoingData[insight.sourceNodeId].outputs[outputId][metric] = getInOutDataObject(
          insight?.MetricsData?.[metric] || 0,
          campaignTotals?.MetricsData?.[metric],
        );
      }
    });
  }

  function setCampaignNodeTotals() {
    if (!rootNodeInsights) {
      const unseenTotals = _reduce(
        metrics,
        (acc, val) => ({
          ...acc,
          [val]: {
            value: campaignTotals?.MetricsData?.[val],
            percentageOfTotal: 1,
          },
        }),
        getEmptyInsightsObj(metrics),
      );

      result.outgoingData[CAMPAIGN_NODE_ID] = {
        outputs: {
          0: getEmptyInsightsObj(metrics),
          [UNSEEN_NODES_OUTPUT_ID]: unseenTotals,
        },
        totals: unseenTotals,
      };

      return;
    }

    const unseenOutputData = {
      ..._reduce(
        metrics,
        (acc, value) => {
          const unseenValue = !PERCENT_METRIC_TYPES.includes(value)
            ? campaignTotals?.MetricsData?.[value] - rootNodeInsights?.MetricsData?.[value]
            : 0;

          return {
            ...acc,
            [value]: {
              value: unseenValue,
              percentageOfTotal: unseenValue / campaignTotals?.MetricsData?.[value],
            },
          };
        },
        {},
      ),
    };

    const campaignInsightsRootData = {
      ..._reduce(
        metrics,
        (acc, value) => ({
          ...acc,
          [value]: {
            value: rootNodeInsights?.MetricsData?.[value],
            percentageOfTotal:
              rootNodeInsights?.MetricsData?.[value] / campaignTotals?.MetricsData?.[value],
          },
        }),
        getEmptyInsightsObj(metrics),
      ),
    };

    result.outgoingData[CAMPAIGN_NODE_ID] = {
      outputs: {
        0: campaignInsightsRootData,
        ...(!!unseenOutputData && {[UNSEEN_NODES_OUTPUT_ID]: unseenOutputData}),
      },
      totals: campaignInsightsRootData,
    };
  }

  const isCampaignMapVersionAnalyticsAlignmentEnabled = enabledFlagsSubject.data.has(
    FeatureFlagKey.EnableCampaignMapVersionAnalyticsAlignment,
  );

  if (isCampaignMapVersionAnalyticsAlignmentEnabled) {
    Object.keys(result.outgoingData).forEach((nodeId) => {
      result.outgoingData[+nodeId].outputs[UNSEEN_NODES_OUTPUT_ID] =
        result.outgoingData[+nodeId].outputs[UNSEEN_NODES_OUTPUT_ID] || {};

      if (+nodeId !== CAMPAIGN_NODE_ID) {
        const outputs = result.outgoingData[+nodeId].outputs;
        metrics.forEach((metric) => {
          if (PERCENT_METRIC_TYPES.includes(metric)) {
            outputs[UNSEEN_NODES_OUTPUT_ID][metric] = {
              value: 0,
              percentageOfTotal: 0,
            };
          } else {
            const total = _sumBy(_map(outputs), (output) => output?.[metric].value ?? 0);
            const nodeValue = result.incomingData[+nodeId]?.totals[metric].value;
            const fallbackValue =
              result.outgoingData[+nodeId]?.outputs[FALLBACK_OUTPUT_ID]?.[metric].value;
            const unseenValue = !PERCENT_METRIC_TYPES.includes(metric)
              ? nodeValue - total - fallbackValue
              : 0;

            outputs[UNSEEN_NODES_OUTPUT_ID][metric] = {
              value: unseenValue,
              percentageOfTotal: unseenValue / (nodeValue || unseenValue),
            };
          }
        });
      }
    });
  }

  return result;
}

function toClientExperimentsList(data: RemoteExperiment[]) {
  return _map(data, (experiment) => {
    const {startDate, archiveDate, lastActivityDate} = experiment;

    return {
      ...experiment,
      startDate: startDate ? DateTime.fromISO(startDate) : null,
      archiveDate: archiveDate ? DateTime.fromISO(archiveDate) : null,
      lastActivityDate: lastActivityDate ? DateTime.fromISO(lastActivityDate) : null,
    };
  });
}

function toClientDetailedExperimentData(
  experiment: RemoteDetailedExperiment,
  campaignEnums: Record<string, CLEnumsMapper>,
) {
  const {stages, startDate, archiveDate, configuration, lastActivityDate, uiVariantsGraphData} =
    experiment;

  return {
    ...experiment,
    optimizeBy:
      campaignEnums[CLEnumKeys.CampaignPage.OptimizeByEnum].byValue[configuration.optimizeBy]
        .uiName,
    startDate: startDate ? DateTime.fromISO(startDate) : null,
    archiveDate: archiveDate ? DateTime.fromISO(archiveDate) : null,
    lastActivityDate: lastActivityDate ? DateTime.fromISO(lastActivityDate) : null,
    stages: _map(stages, (stage: RemoteDetailedExperimentStagesData) => {
      const {start, end, variantsData} = stage;

      return {
        ...stage,
        end: end ? DateTime.fromISO(end) : null,
        start: start ? DateTime.fromISO(start) : null,
        variantsData: _map(variantsData, (variant) => {
          const {dropDate} = variant;

          return {
            ...variant,
            dropDate: dropDate ? DateTime.fromISO(dropDate) : null,
          };
        }),
      };
    }),
    uiVariantsGraphData: _map(
      uiVariantsGraphData,
      (graphData: RemoteDetailedExperimentGraphData) => {
        const {dataPerDate} = graphData;

        return {
          ...graphData,
          dataPerDate: _map(dataPerDate, (data) => {
            const {event_date} = data;

            return {
              ...data,
              date: event_date ? DateTime.fromISO(event_date) : null,
            };
          }),
        };
      },
    ),
  };
}

function toClientAvailableFilter(filters: RemoteAvailableFilter[]): CLSelectorOption[] {
  const allowedFilters = _filter(filters, ({value}) => !!getAnalyticsUIName(value));

  return _map(allowedFilters, ({value, category}) => ({
    value,
    category,
    label: getAnalyticsUIName(value),
  }));
}

function toClientAvailableMetricsForPreset(
  remoteMetrics: RemoteAvailableMetricsForPreset,
  selectedMetrics: MetricValue[],
): AvailableMetricsForPreset {
  return new Set([
    ...(remoteMetrics.supportedMetrics ?? []),
    ...(remoteMetrics.supportedCustomConversion ?? []),
    ...selectedMetrics,
  ]);
}

function toClientGeneralStatistics(
  remoteGeneralStatistics: RemoteGeneralStatistics,
): AvailableMetricWithTotal[] {
  return _map(remoteGeneralStatistics.Metrics, (metricValue, metric: MetricValue) => ({
    value: metric,
    name: getAnalyticsUIName(metric),
    total: isPercentageMetric(metric) ? +(metricValue * 100).toFixed(2) : metricValue,
  }));
}

export {
  toClientAvailableFilter,
  toClientAvailableMetricsForPreset,
  toClientDetailedExperimentData,
  toClientExperimentsList,
  toLocalFlightControlInsights,
  toNodesAggregatedInsights,
  toClientGeneralStatistics,
};
